import { FleetTaskGroup, PermissionCode, type FleetTaskGroupValue } from '@gb-ticket/shared-types';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { formatDeDate } from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { FleetManualTaskActions } from '@/products/fleet/features/fleet-manual-task-actions';
import {
  useCreateFleetManualTask,
  useFleetPeople,
  useFleetTasks,
} from '@/products/fleet/features/use-fleet';
import { FLEET_ROOT } from '@/products/fleet/fleet-paths';

const GROUPS: { key: FleetTaskGroupValue; title: string }[] = [
  { key: FleetTaskGroup.OVERDUE, title: 'Überfällig' },
  { key: FleetTaskGroup.UPCOMING, title: 'Bald fällig' },
  { key: FleetTaskGroup.DAMAGES, title: 'Offene Schäden' },
  { key: FleetTaskGroup.MILEAGE, title: 'Kilometer fehlt' },
  { key: FleetTaskGroup.HELD, title: 'Außer Betrieb' },
];

function TaskGroupSection({
  group,
  title,
  focused,
}: {
  group: FleetTaskGroupValue;
  title: string;
  focused: boolean;
}) {
  const [page, setPage] = useState(1);
  const tasks = useFleetTasks(true, { group, page });
  const items = tasks.data?.items ?? [];

  useEffect(() => {
    if (!focused || tasks.isPending || items.length === 0) {
      return;
    }
    const node = document.getElementById(`fleet-tasks-${group}`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'start' });
    }
  }, [focused, group, items.length, tasks.isPending]);

  if (!tasks.isPending && items.length === 0) {
    return null;
  }

  return (
    <section
      id={`fleet-tasks-${group}`}
      className={focused ? 'scroll-mt-4 space-y-2 rounded-md ring-1 ring-primary/30' : 'space-y-2'}
    >
      <h2 className="text-base font-semibold">{title}</h2>
      {tasks.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {items.length > 0 ? (
        <ul className={`${fleetTableFrameClassName} divide-y`}>
          {items.map((item) => (
            <li key={item.id}>
              {item.kind === 'manual' ? (
                <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="font-medium">{item.title}</span>
                  <span className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <span>
                      {item.subjectLabel}
                      {item.dueOn ? ` · ${formatDeDate(item.dueOn)}` : ''}
                      {' · Offen'}
                    </span>
                    <FleetManualTaskActions task={item} />
                  </span>
                </div>
              ) : (
                <Link
                  to={item.subjectHref}
                  className="flex flex-col gap-1 p-3 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="font-medium">{item.title}</span>
                  <span className="text-sm text-muted-foreground">
                    {item.subjectLabel}
                    {item.dueOn ? ` · ${formatDeDate(item.dueOn)}` : ''}
                    {item.overdue && group !== FleetTaskGroup.OVERDUE ? ' · Überfällig' : ''}
                  </span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {tasks.data && tasks.data.total > tasks.data.pageSize ? (
        <ListPagination
          page={tasks.data.page}
          pageSize={tasks.data.pageSize}
          total={tasks.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </section>
  );
}

export function FleetTasksPage() {
  usePageTitle('Aufgaben');
  const me = useMe();
  const [search] = useSearchParams();
  const focus = search.get('group');
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const [view, setView] = useState<'open' | 'cancelled'>('open');

  if (me.data && !manage) {
    return <Navigate to={FLEET_ROOT} replace />;
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Aufgaben"
        description="Offene Fristen, Schäden und Kilometerlücken."
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          className="min-h-11"
          variant={view === 'open' ? 'default' : 'outline'}
          aria-pressed={view === 'open'}
          onClick={() => setView('open')}
        >
          Offen
        </Button>
        <Button
          type="button"
          className="min-h-11"
          variant={view === 'cancelled' ? 'default' : 'outline'}
          aria-pressed={view === 'cancelled'}
          onClick={() => setView('cancelled')}
        >
          Storniert
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Fristen, Schäden und Kilometerstände werden berechnet und lassen sich nicht stornieren. Eine
        freie Aufgabe kann den Status Storniert bekommen. Sie gilt dann nicht als erledigt und
        bleibt nach dem Neuladen unter Storniert.
      </p>
      {view === 'open' ? <ManualTaskForm /> : null}
      {view === 'open'
        ? GROUPS.map((group) => (
            <TaskGroupSection
              key={group.key}
              group={group.key}
              title={group.title}
              focused={focus === group.key}
            />
          ))
        : null}
      {view === 'cancelled' ? <CancelledTaskSection /> : null}
    </div>
  );
}

function CancelledTaskSection() {
  const [page, setPage] = useState(1);
  const tasks = useFleetTasks(true, { status: 'cancelled', page });
  const items = tasks.data?.items ?? [];
  const total = tasks.data?.total ?? 0;

  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold">Storniert ({total})</h2>
      {tasks.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {!tasks.isPending && items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine stornierten Aufgaben.</p>
      ) : null}
      {items.length > 0 ? (
        <ul className={`${fleetTableFrameClassName} divide-y`}>
          {items.map((item) => (
            <li
              key={item.id}
              className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="font-medium">{item.title}</span>
              <span className="text-sm text-muted-foreground">
                {item.subjectLabel}
                {item.dueOn ? ` · ${formatDeDate(item.dueOn)}` : ''} · Storniert
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {tasks.data && tasks.data.total > tasks.data.pageSize ? (
        <ListPagination
          page={tasks.data.page}
          pageSize={tasks.data.pageSize}
          total={tasks.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </section>
  );
}

function ManualTaskForm() {
  const create = useCreateFleetManualTask();
  const [title, setTitle] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [query, setQuery] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const people = useFleetPeople(query, { minLength: 2, scope: 'active' });

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!assigneeId) {
      toast.error('Bitte eine Person wählen.');
      return;
    }
    try {
      await create.mutateAsync({ title: title.trim(), dueOn, assigneeId });
      setTitle('');
      setDueOn('');
      setQuery('');
      setAssigneeId('');
      setAssigneeName('');
      toast.success('Aufgabe zugewiesen.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Aufgabe konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className={`${fleetPanelClassName} space-y-3`} onSubmit={(event) => void onSubmit(event)}>
      <h2 className="text-base font-semibold">Aufgabe zuweisen</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="task-title">Titel</Label>
          <Input
            id="task-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="task-due">Fällig am</Label>
          <Input
            id="task-due"
            type="date"
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
            required
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="task-person">Person</Label>
        <Input
          id="task-person"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Name oder E-Mail, mindestens 2 Zeichen"
        />
        {assigneeName ? (
          <p className="text-sm text-muted-foreground">Gewählt: {assigneeName}</p>
        ) : null}
        {people.data && people.data.items.length > 0 ? (
          <ul className="space-y-1">
            {people.data.items.map((person) => (
              <li key={person.id}>
                <Button
                  type="button"
                  size="sm"
                  variant={assigneeId === person.id ? 'default' : 'outline'}
                  onClick={() => {
                    setAssigneeId(person.id);
                    setAssigneeName(person.displayName);
                  }}
                >
                  {person.displayName}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <Button type="submit" disabled={create.isPending}>
        Zuweisen
      </Button>
    </form>
  );
}
